import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  AlignLeft,
  ExternalLink,
  MessageSquare,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  FEATURE_REQUEST_BOARD_STATUSES,
  featureRequestCategoryLabel,
  featureRequestStatusMeta,
  upcomingQuarters,
} from "../constants/featureRequests";
import { useFeatureRequestTopics } from "../hooks/useFeatureRequestTopics";
import { useAuth } from "../hooks/useAuth";
import { apiService, extractApiError } from "../services/api";
import type { FeatureRequest, FeatureRequestComment } from "../types";
import FeatureRequestAIAnalysisCard from "./FeatureRequestAIAnalysisCard";
import LoadingSpinner from "./LoadingSpinner";

interface FeatureRequestDetailModalProps {
  request: FeatureRequest;
  isAdmin: boolean;
  otherRequests: FeatureRequest[];
  onClose: () => void;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function FeatureRequestDetailModal({
  request,
  isAdmin,
  otherRequests,
  onClose,
}: FeatureRequestDetailModalProps) {
  const { topics } = useFeatureRequestTopics();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [etaQuarter, setEtaQuarter] = useState(request.etaQuarter);
  const [officialResponse, setOfficialResponse] = useState(
    request.officialResponse,
  );
  const [category, setCategory] = useState<string>(request.category);
  const [mergeTargetId, setMergeTargetId] = useState("");

  const commentsKey = ["feature-request-comments", request.id];
  const commentsQuery = useQuery<FeatureRequestComment[]>(
    commentsKey,
    async () => {
      const res = await apiService.getFeatureRequestComments(request.id);
      return (res.data?.data?.items ?? []) as FeatureRequestComment[];
    },
    { refetchOnWindowFocus: false },
  );

  const addCommentMutation = useMutation(
    () => apiService.addFeatureRequestComment(request.id, newComment.trim()),
    {
      onSuccess: () => {
        setNewComment("");
        queryClient.invalidateQueries(commentsKey);
        queryClient.invalidateQueries("feature-requests");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const deleteCommentMutation = useMutation(
    (commentId: string) =>
      apiService.deleteFeatureRequestComment(request.id, commentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(commentsKey);
        queryClient.invalidateQueries("feature-requests");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const statusMutation = useMutation(
    (status: string) => apiService.setFeatureRequestStatus(request.id, status),
    {
      onSuccess: () => {
        toast.success("Status updated");
        queryClient.invalidateQueries("feature-requests");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const updateMutation = useMutation(
    () =>
      apiService.updateFeatureRequest(request.id, {
        etaQuarter,
        officialResponse,
        category,
      }),
    {
      onSuccess: () => {
        toast.success("Feature request updated");
        queryClient.invalidateQueries("feature-requests");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const mergeMutation = useMutation(
    () => apiService.mergeFeatureRequest(request.id, mergeTargetId),
    {
      onSuccess: () => {
        toast.success("Feature request merged");
        queryClient.invalidateQueries("feature-requests");
        onClose();
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const deleteMutation = useMutation(
    () => apiService.deleteFeatureRequest(request.id),
    {
      onSuccess: () => {
        toast.success("Feature request deleted");
        queryClient.invalidateQueries("feature-requests");
        onClose();
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const handleMerge = () => {
    if (!mergeTargetId) return;
    const target = otherRequests.find((r) => r.id === mergeTargetId);
    if (
      window.confirm(
        `Merge "${request.title}" into "${target?.title ?? "the selected request"}"? Votes and comments will be moved and this request will be removed.`,
      )
    ) {
      mergeMutation.mutate();
    }
  };

  const handleDelete = () => {
    if (
      window.confirm(
        `Delete "${request.title}"? This removes it from the board and closes the GitHub issue.`,
      )
    ) {
      deleteMutation.mutate();
    }
  };

  const canDeleteComment = (comment: FeatureRequestComment) =>
    isAdmin ||
    (user?.email ?? "").toLowerCase() === comment.authorEmail.toLowerCase();

  const quarterOptions = upcomingQuarters();
  if (etaQuarter && !quarterOptions.includes(etaQuarter)) {
    quarterOptions.unshift(etaQuarter);
  }

  const statusMeta = featureRequestStatusMeta(request.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {request.title}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {statusMeta && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusMeta.badgeClasses}`}
                >
                  {statusMeta.label}
                </span>
              )}
              {request.etaQuarter && (
                <span className="inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900 px-2 py-0.5 text-xs text-primary-800 dark:text-primary-200">
                  ETA {request.etaQuarter}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {request.requesterName || request.requesterEmail} ·{" "}
              {formatDateTime(request.createdAt)} ·{" "}
              {featureRequestCategoryLabel(request.category)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon flex-shrink-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <AlignLeft className="h-4 w-4" />
              Description
            </h3>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {request.description}
            </p>
          </div>

          {request.aiAnalysis && (
            <FeatureRequestAIAnalysisCard
              analysis={request.aiAnalysis}
              analysisBy={request.aiAnalysisBy ?? ""}
              analysisAt={request.aiAnalysisAt ?? null}
              formatDateTime={formatDateTime}
            />
          )}

          {isAdmin && (
            <a
              href={request.githubIssueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 underline"
            >
              View GitHub issue #{request.githubIssueNumber}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}

          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <MessageSquare className="h-4 w-4" />
              Comments
            </h3>

            {commentsQuery.isLoading && (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            )}

            {commentsQuery.data &&
              commentsQuery.data.length === 0 &&
              !request.officialResponse && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No comments yet.
                </p>
              )}

            {(request.officialResponse ||
              (commentsQuery.data && commentsQuery.data.length > 0)) && (
              <ul className="mt-2 space-y-3">
                {request.officialResponse && (
                  <li className="rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 p-3">
                    <p className="text-xs font-semibold uppercase text-primary-800 dark:text-primary-200">
                      Official response
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                      {request.officialResponse}
                    </p>
                    {request.officialResponseBy && (
                      <p className="mt-2 text-xs text-primary-700 dark:text-primary-300">
                        - {request.officialResponseBy}
                        {request.officialResponseAt
                          ? ` · ${formatDateTime(request.officialResponseAt)}`
                          : ""}
                      </p>
                    )}
                  </li>
                )}
                {commentsQuery.data?.map((comment) => (
                  <li
                    key={comment.id}
                    className={
                      comment.isOfficial
                        ? "rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 p-3"
                        : "rounded-md bg-gray-50 dark:bg-gray-700/50 p-3"
                    }
                  >
                    {comment.isOfficial && (
                      <p className="text-xs font-semibold uppercase text-primary-800 dark:text-primary-200">
                        Official response
                      </p>
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {comment.authorName || comment.authorEmail} ·{" "}
                        {formatDateTime(comment.createdAt)}
                      </p>
                      {canDeleteComment(comment) && (
                        <button
                          type="button"
                          onClick={() =>
                            deleteCommentMutation.mutate(comment.id)
                          }
                          className="btn-icon text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          aria-label="Delete comment"
                          disabled={deleteCommentMutation.isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {comment.body}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            <form
              className="mt-3 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (newComment.trim()) addCommentMutation.mutate();
              }}
            >
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="input min-h-[4rem]"
                placeholder="Add a comment"
                maxLength={2000}
                disabled={addCommentMutation.isLoading}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={addCommentMutation.isLoading || !newComment.trim()}
              >
                {addCommentMutation.isLoading ? "Posting..." : "Post comment"}
              </button>
            </form>
          </div>

          {isAdmin && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Admin
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="featureRequestEta"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    ETA
                  </label>
                  <select
                    id="featureRequestEta"
                    value={etaQuarter}
                    onChange={(e) => setEtaQuarter(e.target.value)}
                    className="input"
                  >
                    <option value="">No ETA</option>
                    {quarterOptions.map((quarter) => (
                      <option key={quarter} value={quarter}>
                        {quarter}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="featureRequestCategoryEdit"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Category
                  </label>
                  <select
                    id="featureRequestCategoryEdit"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="input"
                  >
                    {topics.map((option) => (
                      <option key={option.slug} value={option.slug}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="featureRequestBoardStatus"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Board status
                  </label>
                  <select
                    id="featureRequestBoardStatus"
                    value={request.projectStatus || ""}
                    onChange={(e) => statusMutation.mutate(e.target.value)}
                    className="input"
                    disabled={statusMutation.isLoading}
                  >
                    <option value="" disabled>
                      Select status
                    </option>
                    {FEATURE_REQUEST_BOARD_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Moves the card on the GitHub Project board immediately.
                  </p>
                </div>
              </div>

              <div>
                <label
                  htmlFor="featureRequestOfficialResponse"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Official response
                </label>
                <textarea
                  id="featureRequestOfficialResponse"
                  value={officialResponse}
                  onChange={(e) => setOfficialResponse(e.target.value)}
                  className="input min-h-[5rem]"
                  placeholder="Visible to everyone; the requester is notified by email"
                  maxLength={5000}
                />
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? "Saving..." : "Save changes"}
              </button>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                <div>
                  <label
                    htmlFor="featureRequestMergeTarget"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Merge into another request
                  </label>
                  <div className="flex gap-2">
                    <select
                      id="featureRequestMergeTarget"
                      value={mergeTargetId}
                      onChange={(e) => setMergeTargetId(e.target.value)}
                      className="input"
                    >
                      <option value="">Select a request</option>
                      {otherRequests.map((other) => (
                        <option key={other.id} value={other.id}>
                          {other.title}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline flex-shrink-0"
                      onClick={handleMerge}
                      disabled={!mergeTargetId || mergeMutation.isLoading}
                    >
                      {mergeMutation.isLoading ? "Merging..." : "Merge"}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline text-red-600 dark:text-red-400"
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading
                    ? "Deleting..."
                    : "Delete feature request"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
